// Layer 3 (Transformations) for PortfolioScene: pure projection of the
// PROJECT_TILES seed — filter, sort, and headline metrics. No React, no
// engine mutations. Mirrors ai-foundation's computePortfolioMetrics + the
// ProjectsGrid visible-list memo (src/app/(app)/projects/ProjectsGrid.tsx).

import type { ProjectTile } from '@/versions/v1/shared/project-seed';

export type PortfolioSortKey = 'activity' | 'name' | 'created';

export const PORTFOLIO_SORT_LABELS: Record<PortfolioSortKey, string> = {
  activity: 'Activity',
  name: 'Name',
  created: 'Created',
};

export type PortfolioMetrics = {
  totalProjects: number;
  activeChats: number;
  scopedFiles: number;
  sharedProjects: number;
  recentlyUpdated: number;
};

export function computePortfolioMetrics(
  tiles: ProjectTile[],
  now: number,
): PortfolioMetrics {
  const weekMs = 7 * 24 * 60 * 60 * 1000;

  let activeChats = 0;
  let scopedFiles = 0;
  let sharedProjects = 0;
  let recentlyUpdated = 0;

  for (const tile of tiles) {
    activeChats += tile.threadCount;
    scopedFiles += tile.fileCount;
    if (tile.viewerRole !== 'owner') sharedProjects += 1;
    const lastActivityAt = tile.lastActivityAt ?? tile.updatedAt;
    if (now - lastActivityAt < weekMs) recentlyUpdated += 1;
  }

  return {
    totalProjects: tiles.length,
    activeChats,
    scopedFiles,
    sharedProjects,
    recentlyUpdated,
  };
}

export function filterAndSortTiles(
  tiles: ProjectTile[],
  query: string,
  sort: PortfolioSortKey,
): ProjectTile[] {
  const q = query.trim().toLowerCase();
  const filtered = q
    ? tiles.filter((t) =>
        `${t.name} ${t.description}`.toLowerCase().includes(q),
      )
    : tiles;

  const sorted = [...filtered];
  switch (sort) {
    case 'name':
      sorted.sort((a, b) => a.name.localeCompare(b.name));
      break;
    case 'created':
      sorted.sort((a, b) => b.createdAt - a.createdAt);
      break;
    case 'activity':
    default:
      sorted.sort(
        (a, b) =>
          (b.lastActivityAt ?? b.updatedAt) - (a.lastActivityAt ?? a.updatedAt),
      );
      break;
  }
  return sorted;
}

export function formatViewerRole(role: ProjectTile['viewerRole']): string {
  switch (role) {
    case 'owner':
      return 'You';
    case 'member':
      return 'Member';
    case 'viewer':
      return 'Viewer';
    default:
      return '—';
  }
}

export function formatRelativeTime(timestamp: number, now: number): string {
  const diff = now - timestamp;
  const minutes = Math.floor(diff / 60_000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(timestamp).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}
